import { useCallback, useEffect, useRef, useState } from 'react'

import { useRoundMusic } from '../games/gameSound.js'
import { boardClassName, tvBoardClassName } from '../games/memoryLayout.js'
import { RaceTrack } from '../games/RaceTrack.js'
import { LANE, RACE_ART, racerPicture } from '../games/raceArt.js'
import { digitFrom, useGameKeys } from '../games/useGameKeys.js'
import { useLeaveOnEscape } from '../games/useLeaveOnEscape.js'
import { KeyCap } from '../components/KeyCap.js'
import { KeyLegend, type Shortcut } from '../components/KeyLegend.js'
import { PlayAgain } from '../games/PlayAgain.js'
import { GameCardFace, PICKER_GRID, PlainCardFace } from '../components/PickerCard.js'
import { LeaveGame } from '../games/LeaveGame.js'
import { HOW_TO_START, ROOM_GAMES } from './hostGames.js'
import { gameAtKey, shortcutFor, shortcutLegend } from './gameShortcuts.js'
import { LEAVE_SHORTCUT } from '../components/shortcuts.js'
import { useBarTitle } from '../layouts/bar-title.js'

import {
  GAMES,
  lettersFound,
  livesDealt,
  RACE_LEVELS,
  TEDDIES,
  teddyPhotoPath,
  wrongGuesses,
  type HangmanView,
  type MathsRaceView,
  type MemoryView,
  type RaceLevel,
  type RoomRoundKey,
  type RoundOptions,
} from '@hazel/shared'

import { fallBackToDrawing } from '../components/teddyPicture.js'
import { Avatar } from '../components/Avatar.js'
import { Button } from '../components/Button.js'
import { Card } from '../components/Card.js'
import { QrCode } from '../realtime/QrCode.js'
import { TurnClock } from '../realtime/TurnClock.js'
import { friendlyRoomError } from '../realtime/types.js'
import { useRoomSocket, type OpenedRoom, type RoomEmit } from '../realtime/useRoomSocket.js'
import type { RoomPlayer, RoomState } from '../realtime/types.js'

/**
 * The key that ends the room, offered wherever the room is.
 *
 * One definition, used by every legend this screen draws. Two copies of a
 * shortcut is how one comes to promise a key the screen does not answer.
 */
const CLOSE_SHORTCUT: Shortcut = { keys: ['X'], does: 'Close the room' }

function joinUrl(code: string): string {
  return `${window.location.origin}/join/${code}`
}

/**
 * Where the television remembers which room it opened.
 *
 * sessionStorage rather than localStorage: this should survive a reload or a
 * dropped connection, but a fresh tab tomorrow should start a new game rather
 * than trying to resume one from last night.
 */
const HOSTED_ROOM_KEY = 'hg_hosted_room'

function rememberHostedRoom(room: OpenedRoom): void {
  try {
    sessionStorage.setItem(HOSTED_ROOM_KEY, JSON.stringify(room))
  } catch {
    // Private browsing, or storage disabled. Hosting still works; it just
    // cannot survive a reconnect.
  }
}

function recallHostedRoom(): OpenedRoom | null {
  try {
    const raw = sessionStorage.getItem(HOSTED_ROOM_KEY)
    if (!raw) return null

    const parsed: unknown = JSON.parse(raw)
    const candidate = parsed as Partial<OpenedRoom>

    return typeof candidate.code === 'string' && typeof candidate.hostToken === 'string'
      ? { code: candidate.code, hostToken: candidate.hostToken }
      : null
  } catch {
    return null
  }
}

function forgetHostedRoom(): void {
  try {
    sessionStorage.removeItem(HOSTED_ROOM_KEY)
  } catch {
    // Nothing to do; see rememberHostedRoom.
  }
}

/** The code, spaced out so it can be read aloud one character at a time. */
function RoomCode({ code }: { code: string }) {
  return (
    <p
      className="font-display text-(length:--text-hero) tracking-[0.2em]"
      aria-label={`Room code: ${[...code].join(' ')}`}
    >
      {code}
    </p>
  )
}

function PlayerTile({ player }: { player: RoomPlayer }) {
  return (
    <li className="flex flex-col items-center gap-2">
      <span className={player.connected ? '' : 'opacity-40'}>
        <Avatar teddy={player.avatarKey} name={player.displayName} size="large" />
      </span>
      <span className="font-display text-(length:--text-heading)">{player.displayName}</span>
      {!player.connected ? (
        <span className="text-ink-muted text-(length:--text-small)">away</span>
      ) : null}
    </li>
  )
}

/*
 * A note on the width these boards take, which they all share.
 *
 * `max-w-5xl` never actually applies: everything on this screen renders under
 * `TvLayout`, which always sets `data-scale="tv"`, so the `max-w-none` beside
 * it wins every time. It is written out anyway so this reads as the same idiom
 * the solo games use, where both halves are live — a bare `w-full` here would
 * look like a different rule rather than the same one.
 *
 * What matters is the `w-full`. Without it a board is as wide as its own
 * contents, which is how Hoppity's race came to be as wide as the word.
 */
function Board({
  game,
  players,
  turnEndsAt,
}: {
  game: HangmanView
  players: readonly RoomPlayer[]
  turnEndsAt: number | null
}) {
  const upNow = players.find((player) => player.id === game.currentPlayerId)
  const lastPlayer = game.lastGuess
    ? players.find((player) => player.id === game.lastGuess?.playerId)
    : undefined

  return (
    <div className="flex w-full max-w-5xl flex-col items-center gap-8 [[data-scale=tv]_&]:max-w-none">
      {/*
        The same race the solo game draws, off the same view.

        This was six hearts — and hardcoded as six, which is the assumption the
        solo game deliberately refuses to make: a round can be dealt with a
        different number of lives and nothing on the view says what that number
        is, so it works the total out as wrong guesses plus lives left. The host
        screen carried the guess the game itself would not, which is the shape
        of DOL-80 all over again.

        And it made one game look like two. A child who plays Hoppity alone and
        then plays him with a cousin should see the same picture.
      */}
      {/*
        Said in words, because the race is silent.

        The hearts this replaces carried an `aria-label` — the only statement
        of how many lives were left that was not a picture. `RaceTrack` is
        `aria-hidden` by design (the screen's one live region says the same
        thing in words elsewhere), so dropping the hearts would have taken the
        fact with them. It goes here instead rather than being lost.
      */}
      <p className="sr-only">{`${game.livesRemaining} lives left`}</p>

      <RaceTrack
        goal={RACE_ART.carrot}
        racers={[
          {
            key: 'hoppity',
            name: 'Hoppity',
            picture: racerPicture('hoppity'),
            steps: lettersFound(game),
            of: game.letters.length,
            tint: LANE.hoppity,
          },
          {
            key: 'daddy',
            name: 'Daddy',
            picture: RACE_ART.daddy,
            steps: wrongGuesses(game),
            of: livesDealt(game),
            tint: LANE.daddy,
          },
        ]}
      />

      <p
        className="font-display flex flex-wrap justify-center gap-3 text-(length:--text-hero) tracking-wider uppercase"
        aria-label={
          game.status === 'playing'
            ? `${game.letters.filter(Boolean).length} of ${game.letters.length} letters found`
            : `The word was ${game.answer ?? ''}`
        }
      >
        {game.letters.map((letter, index) => (
          <span
            key={index}
            className={[
              'border-ink-muted inline-flex min-w-14 justify-center border-b-8',
              letter ? 'border-transparent' : '',
            ].join(' ')}
          >
            {letter ?? ' '}
          </span>
        ))}
      </p>

      <p className="text-ink-muted text-(length:--text-heading)">Clue: {game.hint}</p>

      {/* Who tried what, so the room can follow along rather than just seeing
          letters appear. */}
      {lastPlayer && game.lastGuess ? (
        <p
          className={`text-(length:--text-heading) ${game.lastGuess.correct ? 'text-teal' : 'text-ink-muted'}`}
          role="status"
        >
          {lastPlayer.displayName} tried {game.lastGuess.letter.toUpperCase()} —{' '}
          {game.lastGuess.correct ? 'yes!' : 'not in there'}
        </p>
      ) : null}

      {/*
        Whose go and how long left, on one line at television scale — where the
        room sees both at a glance and, more to the point, where they are not
        taking 219 pixels of a 1080 screen to say two short things. That height
        belongs to the board: with it back, Sky's cards go from 83px to
        comfortably over a hundred, and the board is what the room is watching.
      */}
      {game.status === 'playing' ? (
        <div className="flex flex-col items-center gap-2 [[data-scale=tv]_&]:flex-row [[data-scale=tv]_&]:gap-6">
          <p className="text-(length:--text-title) font-display" role="status">
            {upNow ? `${upNow.displayName}'s go` : 'Waiting…'}
          </p>
          <TurnClock endsAt={turnEndsAt} size="large" />
        </div>
      ) : (
        <p className="text-(length:--text-title) font-display" role="status">
          {game.status === 'won'
            ? 'You got it!'
            : `The word was ${game.answer?.toUpperCase() ?? ''}`}
        </p>
      )}

      {game.guessed.length > 0 ? (
        <p
          className="text-ink-muted text-(length:--text-heading) tracking-widest uppercase"
          aria-label={`Letters tried: ${game.guessed.join(', ')}`}
        >
          {game.guessed.join(' ')}
        </p>
      ) : null}
    </div>
  )
}

/**
 * The race, as the room sees it.
 *
 * Everybody answering at once, and nobody set against anybody else. What the
 * room gets is added up between them at the end — they were doing the same
 * questions at the same time, and adding it up is the version of that where
 * nobody comes last.
 *
 * While it is running the television shows how far along each player is, and
 * deliberately not how they are doing. "How far along" is questions worked
 * through rather than questions got right: those would otherwise be the same
 * number, since a round ends when you have ten right, so showing
 * progress-as-answers-right would have been a running tally under a kinder
 * name. Attempts rise faster when somebody is struggling, so it cannot be read
 * as one either way.
 */
function RaceBoard({ game, players }: { game: MathsRaceView; players: readonly RoomPlayer[] }) {
  const nameOf = (playerId: string) =>
    players.find((player) => player.id === playerId)?.displayName ?? 'Someone'

  const finished = game.status !== 'playing'

  return (
    <div className="flex w-full max-w-5xl flex-col items-center gap-8 [[data-scale=tv]_&]:max-w-none">
      <p className="font-display text-(length:--text-title)" role="status">
        {finished ? 'All done!' : 'Everyone go!'}
      </p>

      {finished && game.together ? (
        // One number, for the room.
        <p className="text-teal font-display text-(length:--text-hero) tabular-nums">
          {`Between you: ${game.together.correct} of ${game.together.outOf}`}
        </p>
      ) : null}

      <ul className="flex list-none flex-col gap-6 p-0">
        {game.players.map((player) => (
          <li key={player.playerId} className="flex items-center gap-6">
            <span className="font-display min-w-64 text-(length:--text-heading)">
              {nameOf(player.playerId)}
            </span>

            {finished ? (
              <span className="text-ink-muted text-(length:--text-heading)">done</span>
            ) : (
              <span
                className="text-ink-muted text-(length:--text-heading)"
                // Read out as words rather than as a row of dots, which is
                // meaningless to a screen reader.
                aria-label={`${nameOf(player.playerId)} has done ${player.asked} ${
                  player.asked === 1 ? 'question' : 'questions'
                }`}
              >
                <span aria-hidden="true">{'•'.repeat(Math.min(player.asked, 20)) || '…'}</span>
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * The memory board, big enough to read from the sofa.
 *
 * Everybody sees the same thing here, unlike the race: what is face up is face
 * up, and the rest is nobody's to see including whoever is up. The board *is*
 * the game, so it goes on the television and the phones only send taps.
 */
function MemoryBoard({
  game,
  players,
  turnEndsAt,
}: {
  game: MemoryView
  players: readonly RoomPlayer[]
  turnEndsAt: number | null
}) {
  const upNow = players.find((player) => player.id === game.currentPlayerId)
  // Asked of the one table that knows, rather than worked out here. See
  // `boardClassName` for what this used to be and why it looked right.
  const columns = boardClassName(game.cards.length)
  const fillsTheScreen = tvBoardClassName(game.cards.length)

  return (
    /*
     * `h-full` at television scale, so the grid below has something to fill.
     *
     * Twenty cards at their natural size run 275px past a 1080 screen once
     * three people are seated and there are three scores under it — which is
     * the room this is actually for. The solo board has never had that problem
     * because it divides the height it is given; this one sized itself to its
     * own contents and let the frame deal with the consequences.
     */
    <div className="flex w-full max-w-5xl flex-col items-center gap-8 [@media(min-height:900px)]:min-h-0 [@media(min-height:900px)]:flex-1 [[data-scale=tv]_&]:max-w-none [[data-scale=tv]_&]:gap-4">
      {/*
        The same treatment the solo board uses, from the same table: `flex-1`
        takes the height left over, `min-h-0` lets it actually shrink, and the
        aspect from `tvClassName` keeps the cards square while it does.
      */}
      <ul
        className={`grid list-none gap-4 p-0 ${columns} [@media(min-height:900px)]:min-h-0 [@media(min-height:900px)]:w-auto [@media(min-height:900px)]:max-w-full [@media(min-height:900px)]:flex-1 [[data-scale=tv]_&]:gap-2 ${fillsTheScreen}`}
      >
        {game.cards.map((card) => (
          <li
            key={card.card}
            className={[
              /*
                A fixed size everywhere but the television, where the card
                fills whatever cell the grid gives it.
                
                `size-32` alone is what made the board overflow: the grid
                shrank to the height it was allotted and the cards did not,
                so twenty of them spilled over each other and over the line
                beneath. The aspect on the grid keeps the cells square, so
                filling one keeps the card square too.
              */
              'flex size-32 items-center justify-center overflow-hidden',
              '[@media(min-height:900px)]:size-full',
              'rounded-(--radius-control) border-4 p-1',
              card.matched ? 'border-teal opacity-70' : 'border-border bg-surface-sunken',
            ].join(' ')}
          >
            {card.teddy ? (
              <TeddyFace teddy={card.teddy} />
            ) : (
              <span
                aria-hidden="true"
                className="font-display text-ink-muted text-(length:--text-hero)"
              >
                ?
              </span>
            )}
          </li>
        ))}
      </ul>

      <p className="text-ink-muted text-(length:--text-heading)">
        {`${game.pairsFound} of ${game.pairsTotal} pairs · ${game.flips} cards turned`}
      </p>

      {/*
        Whose go and how long left, on one line at television scale — where the
        room sees both at a glance and, more to the point, where they are not
        taking 219 pixels of a 1080 screen to say two short things. That height
        belongs to the board: with it back, Sky's cards go from 83px to
        comfortably over a hundred, and the board is what the room is watching.
      */}
      {game.status === 'playing' ? (
        <div className="flex flex-col items-center gap-2 [[data-scale=tv]_&]:flex-row [[data-scale=tv]_&]:gap-6">
          <p className="text-(length:--text-title) font-display" role="status">
            {upNow ? `${upNow.displayName}'s go` : 'Waiting…'}
          </p>
          <TurnClock endsAt={turnEndsAt} size="large" />
        </div>
      ) : (
        <p className="text-(length:--text-title) font-display" role="status">
          Every pair found!
        </p>
      )}

      {/* Who has found what. Unlike the maths race this is not withheld: the
          board is in front of everybody anyway, and a pair that has been found
          is visibly gone. Hiding the tally would hide nothing. */}
      <ul className="flex list-none flex-wrap justify-center gap-8 p-0">
        {players.map((player) => (
          <li key={player.id} className="flex items-center gap-3">
            <Avatar teddy={player.avatarKey} name={player.displayName} />
            <span className="font-display text-(length:--text-heading)">
              {`${player.displayName}: ${game.playerPairs[player.id] ?? 0}`}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/** A teddy's photograph, with its name under it. See MemoryCard for why. */
function TeddyFace({ teddy }: { teddy: NonNullable<MemoryView['cards'][number]['teddy']> }) {
  const meta = TEDDIES[teddy]

  return (
    <span className="flex size-full flex-col items-center justify-center gap-1">
      <img
        src={teddyPhotoPath(meta.key)}
        alt=""
        className="min-h-0 flex-1 object-contain"
        onError={(event) => fallBackToDrawing(event.currentTarget, meta.key)}
      />
      <span aria-hidden="true" className="font-display text-(length:--text-small) leading-tight">
        {meta.name}
      </span>
    </span>
  )
}

/**
 * What to play next.
 *
 * A room is a group of people round a television, not a single game — they
 * finish a word and want to do sums next without everybody rejoining. So the
 * choice lives here rather than being fixed when the room opens.
 */

/**
 * One of the things a room can start, with the number that starts it.
 *
 * The keycap and the key come from the same `at`, so a button cannot come to
 * advertise a number that does something else — which is the failure this app
 * has shipped before, in a legend that went on offering a key after the thing
 * it did had gone.
 */
/**
 * Words for the difficulty settings, since "level 4" means nothing to anybody.
 */
const RACE_LABELS: Readonly<Record<RaceLevel, string>> = {
  'warm-up': 'Warm up',
  'just-right': 'Just right',
  'really-tricky': 'Really tricky',
}

/**
 * What the caption under each game's picture says a press will do.
 *
 * It used to read "Another word" on Hoppity's card once a round had been
 * played, because the menu was drawn under the finished board. It is not any
 * more — a result is its own screen now, and that is where another go is
 * offered (DOL-101). The menu only ever appears with no round in progress, so
 * the card can go back to saying what the game is.
 */
function captionFor(key: RoomRoundKey): string {
  if (key === 'maths') return 'Pick how hard, then everybody races.'

  return GAMES[key].blurb
}

/**
 * Which game the room plays next, asked the way the menu asks it.
 *
 * This was a stack of plain text buttons with no artwork at all, while the
 * picker three feet away was a grid of pictures. Same question, same
 * television, same child, two different answers — and the artwork was already
 * there to be used (DOL-90).
 *
 * The cards come from `ROOM_GAMES`, so a game the room can run appears here by
 * existing rather than by somebody remembering to draw it, and the keys come
 * from the picker's own `shortcutFor`/`gameAtKey`/`shortcutLegend` rather than
 * a private `1–n` — one numbering rule for the app, so a keycap, the key that
 * works and the legend cannot come apart.
 */
function GameChooser({
  onStart,
  onLeave,
}: {
  onStart: (options: RoundOptions) => void
  onLeave: () => void
}) {
  /*
   * The tier step, as state rather than a route.
   *
   * A route would put the room's chooser in the address bar of a television
   * with no address bar, and would need a second screen to know how to resume
   * a room. It is one question with three answers.
   */
  const [asking, setAsking] = useState<RoomRoundKey | null>(null)

  /** Every card, games and the way out alike, numbered once. */
  const cards = ROOM_GAMES.length + 1

  /*
   * State rather than a ref, as on the picker. `tabIndex` is worked out from
   * this during render — a ref would not re-render, so the single tab stop
   * would stay pinned to the first card however far the arrows had moved.
   */
  const [cursor, setCursor] = useState(0)
  const buttons = useRef(new Map<number, HTMLButtonElement>())

  function moveTo(next: number): void {
    // Clamped rather than wrapped, like the picker and the memory board: a
    // cursor that reappears at the other end loses anybody not watching it.
    const target = Math.min(Math.max(next, 0), cards - 1)

    setCursor(target)
    buttons.current.get(target)?.focus()
  }

  function choose(at: number): void {
    // The last card is the way out; the rest are games.
    const key = ROOM_GAMES[at]
    if (!key) return onLeave()

    const how = HOW_TO_START[key]
    if (how.kind === 'tiers') return setAsking(key)

    onStart(how.options)
  }

  useGameKeys((event) => {
    if (asking) return

    // The last rung: nothing left to back out of, so Escape leaves the room.
    if (event.key === 'Escape') {
      event.preventDefault()
      return onLeave()
    }

    const at = gameAtKey(event.key, cards)
    if (at !== null) {
      event.preventDefault()
      return choose(at)
    }

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault()
        return moveTo(cursor - 1)
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault()
        return moveTo(cursor + 1)
      default:
    }
  })

  if (asking) {
    return (
      <ChooseTier
        onChoose={(level) => {
          /*
           * Put the question away before starting.
           *
           * Without this the room is left on the tier step: the chooser is
           * hidden while a round runs, so nothing looks wrong until the round
           * ends and it comes back asking how hard the race should be, over a
           * room that wanted to choose a game. Caught by the keyboard walk,
           * which pressed 2 twice and started two rounds.
           */
          setAsking(null)
          onStart({ game: 'maths', level })
        }}
        onBack={() => setAsking(null)}
      />
    )
  }

  return (
    <div className="flex w-full flex-col items-center gap-4">
      <ul className={`w-full ${PICKER_GRID}`}>
        {ROOM_GAMES.map((key, index) => (
          <li key={key}>
            <button
              type="button"
              /*
               * A plain button rather than `Button`, which brings styling of
               * its own that would fight the card. The picker wraps the same
               * face in a `Link` for the same reason.
               */
              className="group block h-full w-full cursor-pointer border-0 bg-transparent p-0 text-left"
              ref={(element) => {
                if (element) buttons.current.set(index, element)
                else buttons.current.delete(index)
              }}
              // One tab stop for the whole grid, landing where the cursor is.
              tabIndex={index === cursor ? 0 : -1}
              aria-label={
                shortcutFor(index)
                  ? `${GAMES[key].title}. Press ${shortcutFor(index)}`
                  : GAMES[key].title
              }
              onFocus={() => setCursor(index)}
              onClick={() => choose(index)}
            >
              <GameCardFace
                game={GAMES[key]}
                shortcut={shortcutFor(index)}
                caption={captionFor(key)}
              />
            </button>
          </li>
        ))}

        {/*
          The way back to playing alone, as the last card.

          The mirror of the picker's "Play together", which is how she got
          here, and numbered from the same rule as every other card so it
          cannot come to advertise a key that does something else.

          It leaves the screen and keeps the room. Closing the room would turn
          out everybody seated and kill the code their phones are holding — and
          on a keyboard this card is one number away from a game.
        */}
        <li>
          <button
            type="button"
            className="group block h-full w-full cursor-pointer border-0 bg-transparent p-0 text-left"
            ref={(element) => {
              if (element) buttons.current.set(ROOM_GAMES.length, element)
              else buttons.current.delete(ROOM_GAMES.length)
            }}
            tabIndex={ROOM_GAMES.length === cursor ? 0 : -1}
            aria-label={
              shortcutFor(ROOM_GAMES.length)
                ? `Play on your own. Press ${shortcutFor(ROOM_GAMES.length)}`
                : 'Play on your own'
            }
            onFocus={() => setCursor(ROOM_GAMES.length)}
            onClick={onLeave}
          >
            <PlainCardFace shortcut={shortcutFor(ROOM_GAMES.length)} title="Play on your own">
              Back to your own games. The room stays open, so you can come back to it.
            </PlainCardFace>
          </button>
        </li>
      </ul>

      {/*
        Drawn where it is written rather than portalled: the television layout
        has no legend slot, and `KeyLegend` falls back to rendering in place
        when there is none. This is the only thing on the screen telling
        somebody with a keyboard and no mouse that there is anything to press.
      */}
      <KeyLegend
        shortcuts={[
          ...shortcutLegend(cards),
          { keys: ['←', '→'], does: 'Look through them' },
          LEAVE_SHORTCUT,
          CLOSE_SHORTCUT,
        ]}
      />
    </div>
  )
}

/**
 * The end of a round, read as a result rather than as a menu.
 *
 * The board above this is already showing how it went — the word, the pairs,
 * the scores. What was missing was somewhere to go from it: the whole card
 * menu was drawn underneath instead, so the result was something to look past.
 * Solo has never done that, and a child who plays alone already knows the
 * shape of this screen.
 *
 * Two ways on, and the common one is one press. `PlayAgain` is the same
 * component the solo games end with, so the television says "Press Enter to
 * play again" in the same words in the same place.
 */
function RoundOver({ onAgain, onMenu }: { onAgain: () => void; onMenu: () => void }) {
  return (
    <div className="flex flex-col items-center gap-4">
      <PlayAgain label="Another go" does="to play again" onAgain={onAgain} />

      {/*
        The way to a different game, for a host with no keyboard. Hidden at
        television scale, where the legend below offers Escape — the same
        division of labour as `LeaveGame`, and for the same reason: a tablet
        propped up as the host has no Escape key and no legend.
      */}
      <Button variant="secondary" onClick={onMenu} className="[[data-scale=tv]_&]:hidden">
        Choose another game
      </Button>
    </div>
  )
}

/**
 * "Close the room?" — asked only when somebody would lose something.
 *
 * The one irreversible thing this screen can do. Everything else here is a
 * round that can be played again; this ends other people's game and kills the
 * code their phones are holding, and they are not in the room to be consulted.
 *
 * Says how many, because "2 playing" is the fact that decides it and the
 * television is the only place it is written down.
 *
 * Escape backs out, as it does everywhere: this is another rung on the same
 * ladder rather than a modal with rules of its own.
 */
function ConfirmClose({
  seated,
  onConfirm,
  onBack,
}: {
  seated: number
  onConfirm: () => void
  onBack: () => void
}) {
  useGameKeys((event) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      return onBack()
    }

    /*
     * Enter confirms — but only from the keyboard, and never when a button has
     * focus, which `useGameKeys` already steps aside for. Otherwise tabbing to
     * "Keep it open" and pressing Enter would close the room, which is the
     * exact opposite of what the person pressing it asked for.
     */
    if (event.key === 'Enter') {
      event.preventDefault()
      onConfirm()
    }
  })

  return (
    <div className="flex flex-col items-center gap-4">
      <Card className="border-coral flex flex-col items-center gap-4 border-2">
        <h2 className="text-(length:--text-heading)">Close the room?</h2>

        <p className="text-ink-muted text-(length:--text-body)">
          {seated === 1
            ? '1 person is playing. They will be sent back to the join screen, and this code will stop working.'
            : `${seated} people are playing. They will be sent back to the join screen, and this code will stop working.`}
        </p>

        {/*
          The way out comes first, because first in the DOM is first under Tab.

          It read the other way round, under a comment claiming that putting
          "Keep it open" second somehow made it first — which is not a thing
          DOM order does. On the Raspberry Pi there is a keyboard and no mouse
          at all, so Tab-then-Enter is not an edge case there, it is the way
          the screen is used: `useGameKeys` steps aside for Enter when a button
          has focus, so that Enter was a native click on whichever button Tab
          had reached. The one irreversible screen in the room defaulted to
          destroying it.

          Both are plain buttons. A primary-styled "close" would be inviting
          the press.
        */}
        <div className="flex flex-wrap justify-center gap-3">
          <Button variant="secondary" onClick={onBack}>
            Keep it open
          </Button>
          <Button variant="secondary" onClick={onConfirm}>
            Close the room
          </Button>
        </div>
      </Card>

      <KeyLegend
        shortcuts={[
          { keys: ['Enter'], does: 'Close the room' },
          { keys: ['Esc'], does: 'Keep it open' },
        ]}
      />
    </div>
  )
}

/**
 * How hard the race should be, asked once the room has chosen it.
 *
 * The same shape as Sky's board size and Stripey's grid size, so it is a
 * pattern she has met rather than a new one — and the reason the race is one
 * card rather than three: three cards carrying identical Monkey artwork read
 * as three games, which is what the old grouping was written to avoid.
 */
function ChooseTier({
  onChoose,
  onBack,
}: {
  onChoose: (level: RaceLevel) => void
  onBack: () => void
}) {
  useGameKeys((event) => {
    /*
     * Escape backs out one step rather than out of the room.
     *
     * That is the rule `useLeaveOnEscape` already states — back out one step,
     * and out when there is nothing left to back out of — reaching one screen
     * further. Getting stuck on the way in would be a worse trap than getting
     * stuck on the way out.
     */
    if (event.key === 'Escape') {
      event.preventDefault()
      return onBack()
    }

    const digit = digitFrom(event)
    if (!digit) return

    const chosen = RACE_LEVELS[Number(digit) - 1]
    if (chosen !== undefined) {
      event.preventDefault()
      onChoose(chosen)
    }
  })

  return (
    <div className="flex flex-col items-center gap-4">
      <Card className="flex flex-col items-center gap-4">
        <h2 className="text-(length:--text-heading)">How hard should the race be?</h2>

        <div className="flex flex-wrap justify-center gap-3">
          {RACE_LEVELS.map((level: RaceLevel, index: number) => (
            <Button
              key={level}
              variant="secondary"
              className="text-(length:--text-heading)"
              aria-label={`${RACE_LABELS[level]}. Press ${index + 1}`}
              onClick={() => onChoose(level)}
            >
              <span aria-hidden="true" className="[@media(pointer:coarse)]:hidden">
                <KeyCap>{index + 1}</KeyCap>
              </span>
              {RACE_LABELS[level]}
            </Button>
          ))}
        </div>

        {/*
          A way back that is not a key.

          Escape covers the television, where the legend below says so and
          there is a keyboard to press it with. A tablet propped up as the host
          has neither — so somebody who tapped this card by mistake could only
          pick a race they did not want, or leave the room altogether to get
          out of one question. That is the same gap `LeaveGame` was added to
          close, and it hides at television scale for the same reason: there
          the legend already has it.
        */}
        <button
          type="button"
          onClick={onBack}
          className="text-ink-muted hover:text-ink inline-flex min-h-(--size-tap) cursor-pointer items-center justify-center border-0 bg-transparent text-(length:--text-small) [[data-scale=tv]_&]:hidden"
        >
          ← Back to the games
        </button>
      </Card>

      <KeyLegend
        shortcuts={[
          { keys: ['1', '–', String(RACE_LEVELS.length)], does: 'Race at that one' },
          { keys: ['Esc'], does: 'Back to the games' },
        ]}
      />
    </div>
  )
}

function Lobby({ room }: { room: RoomState }) {
  return (
    <div className="flex w-full max-w-5xl flex-col items-center gap-8 [[data-scale=tv]_&]:max-w-none">
      {/*
        Two ways in, said as two.

        This read as "Join at <address>", a QR code, and "or type the code" —
        three things somebody holding a phone had to work out the relationship
        between. It is really two, and one of them has two steps: go to the
        address *and then* type the code, or scan and skip both. The address on
        its own does nothing, which is exactly what was not being said.
      */}
      <p className="text-ink-muted text-(length:--text-heading)">Two ways to join</p>

      <div className="flex flex-wrap items-start justify-center gap-12">
        <div className="flex flex-col items-center gap-2">
          <p className="text-ink-muted text-(length:--text-heading)">Scan this</p>
          <QrCode value={joinUrl(room.code)} size={280} />
        </div>

        <div className="flex flex-col items-center gap-3">
          <p className="text-ink-muted text-(length:--text-heading)">or go to</p>
          {/*
            Wraps and shrinks rather than forcing the page wider.

            At `--text-title` in the display font this is 631px of unbreakable
            text on a 393px phone, so the whole lobby scrolled sideways — and
            `/host` is offered on every device on purpose, because a tablet
            propped against the sugar bowl hosts perfectly well. `break-all`
            rather than `break-words`: an address has no spaces to break at,
            which is the whole of why it overflowed (DOL-91).

            It steps down a size only where it has to. On the television, where
            this is the thing people are typing from across a room, it is
            unchanged.
          */}
          <p className="font-display max-w-full text-center text-(length:--text-heading) break-all sm:text-(length:--text-title)">
            {window.location.host}/join
          </p>
          <p className="text-ink-muted text-(length:--text-heading)">and type</p>
          <RoomCode code={room.code} />
        </div>
      </div>

      {room.players.length === 0 ? (
        // A room nobody has joined should look like it is waiting, not broken.
        <p className="text-ink-muted text-(length:--text-heading)" role="status">
          Waiting for players to join…
        </p>
      ) : (
        <ul className="flex list-none flex-wrap justify-center gap-8 p-0">
          {room.players.map((player) => (
            <PlayerTile key={player.id} player={player} />
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * The screen everybody looks at.
 *
 * No navigation, no account controls, no keyboard. Nobody walks up to a
 * television to click things, so every pixel goes to the board and the only
 * control is the one the person holding the laptop needs.
 */
export function Host() {
  useRoundMusic('room')

  /*
   * Escape, the same as every solo game.
   *
   * The television opened a room and then stayed there — on a Pi in kiosk mode
   * that is the end of the road, since there is no back button and no address
   * bar. Escape means "out of here" on all seven games; there is no reason for
   * this to be the one screen where it means nothing.
   *
   * Safe to leave. The room is remembered and resumed on the next connect, so
   * going back to the menu and returning picks the same room up rather than
   * abandoning whoever is seated in it.
   */
  const leaveOnEscape = useLeaveOnEscape()

  /*
   * Whether the room is being asked about closing.
   *
   * Held by the screen rather than by the chooser, because closing is
   * something the *room* can do rather than something its game menu can. The
   * chooser only appears once somebody has joined — so had this lived there,
   * the one case it is most wanted in would have had no key at all: a room
   * everybody has left, which is exactly when you want to start it over.
   */
  const [confirmingClose, setConfirmingClose] = useState(false)

  useGameKeys((event) => {
    if (confirmingClose) return

    /*
     * A letter, not a digit.
     *
     * The digits start games, and the key that turns four cousins out should
     * not be a neighbour of the one that starts Hangman. Free here today
     * because `gameAtKey` only reaches letters past nine cards — worth saying
     * out loud, since a tenth room game would quietly change that.
     */
    if (event.key === 'x' || event.key === 'X') {
      event.preventDefault()

      /*
       * Asked only when somebody would lose something.
       *
       * An empty room closes on the first press because there is nothing to
       * lose, and a ceremony around no consequence is how people learn to
       * press through a confirmation without reading it — right up until the
       * one that mattered.
       */
      if (room && room.players.length > 0) return setConfirmingClose(true)

      return closeTheRoom()
    }

    /*
     * Enter is another go, the way it is in every solo game — and the way the
     * legend at this scale already teaches ("Press Enter to play again").
     */
    if (event.key === 'Enter' && finished) {
      event.preventDefault()
      return playAgain()
    }

    if (event.key !== 'Escape') return

    /*
     * The chooser answers Escape for itself while it is up, because there it
     * means "back out one step" — out of the tier question first, and out of
     * the room only when there is nothing left to back out of. Two handlers
     * both listening on the window cannot arrange that between them: they are
     * on the same target, so neither can stop the other, and the room left
     * when it had only meant to change its mind about a race.
     */
    if (choosing) return

    event.preventDefault()

    /*
     * Mid-round, Escape ends the round rather than leaving the room.
     *
     * The rung this ladder was missing. Escape used to go from anywhere
     * straight out to the solo games *with the round still running underneath*
     * — so going back into "Play together" resumed a game nobody had chosen,
     * which is not what Escape does anywhere else in the app. Solo ends what
     * you were doing.
     *
     * Ending the game is not ending the room: the seats stay, and the room
     * lands on its own menu ready to pick something else.
     */
    if (room !== null && round !== null) {
      emit('room:endRound', { code: room.code })
      return
    }

    leaveOnEscape(false)
  })

  /**
   * Resume the room this television already opened, or start a new one.
   *
   * Runs on every connect, including reconnects. Calling room:create there
   * would mint a *new* room and silently abandon everybody already seated,
   * putting up a code none of their phones know — which looks like a
   * successful recovery and is the opposite of one.
   */
  const attach = useCallback((emit: RoomEmit) => {
    const hosted = recallHostedRoom()
    if (hosted) emit('room:resume', hosted)
    else emit('room:create')
  }, [])

  // Remember whichever room the server confirms, so a drop can resume it
  // rather than opening a new one.
  const onRoomOpened = useCallback((opened: OpenedRoom) => rememberHostedRoom(opened), [])

  /*
   * Whether this television is the one that asked for the room to close.
   *
   * A ref rather than state: it is read inside a socket callback, and it must
   * be true from the moment the message goes out rather than after a render.
   */
  const closingOnPurpose = useRef(false)

  /**
   * What the last round was dealt with, so another go is the *same* game.
   *
   * A finished round says which game it was but not the settings it was dealt
   * on — a race's tier in particular, which the room chose and would have to
   * choose again. Kept here rather than read back off the view, because the
   * view is deliberately what a player is allowed to know.
   *
   * Null after a reconnect, when this television did not start the round it is
   * looking at. `playAgain` falls back to asking, which is the honest answer:
   * better to offer the question again than to guess a difficulty for a room.
   */
  const lastDealt = useRef<RoundOptions | null>(null)

  /**
   * The room has gone.
   *
   * Two ways to arrive here, and they want opposite things. The server reaping
   * a room, or restarting, leaves a television showing a code that no longer
   * works — so it opens another. A television that *asked* to close its own
   * room wants to leave.
   *
   * The deliberate case waits for this rather than navigating as soon as the
   * message is sent. That looked identical on a laptop and was wrong on a real
   * one: leaving unmounts the socket and disconnects it, and over a network
   * with any latency at all the disconnect beat the message out of the door —
   * so the room was never closed and the phones in the room were never told,
   * while the television walked away looking as though it had worked. Found on
   * staging; a local round trip is too fast to show it.
   */
  const onClosed = useCallback(
    (emit: RoomEmit) => {
      forgetHostedRoom()

      if (closingOnPurpose.current) {
        closingOnPurpose.current = false
        leaveOnEscape(false)
        return
      }

      emit('room:create')
    },
    [leaveOnEscape],
  )

  const { emit, status, room, error } = useRoomSocket({
    onConnect: attach,
    onRoomOpened,
    onClosed,
  })

  // A remembered room that the server no longer has — reaped, or the server
  // restarted. Forget it and open a fresh one rather than showing a dead code.
  useEffect(() => {
    if (error === 'no-such-room') {
      forgetHostedRoom()
      emit('room:create')
    }
  }, [error, emit])

  /*
   * Worked out before the early return, because the Escape handler above needs
   * it: whether the chooser is up decides what Escape means.
   */
  const round = room?.game ?? null
  const roundOver = round !== null && round.view.status !== 'playing'

  /*
   * Three states, not two.
   *
   * A finished round used to count as "choosing", so the whole card menu was
   * drawn underneath the result and the room had to look past what had just
   * happened to see it. Solo does not do that: the end of a round is the
   * screen, and picking a different game is a step you take from it.
   *
   * So: no round is the menu, a finished round is a result, and a running one
   * is the board.
   */
  const choosing = room !== null && room.players.length > 0 && round === null
  const finished = room !== null && room.players.length > 0 && roundOver

  /*
   * The bar names whatever the room is playing, and says "Hazel's Games" the
   * rest of the time — the lobby, the card menu, a finished round.
   *
   * The same rule the solo screens follow, and on this display it is also the
   * only place the name appears at all: the title is on the chooser cards and
   * nowhere else, so until now a television mid-round showed the board of a
   * game it never named. It is this screen's only level-1 heading too.
   *
   * Called before the early returns below, because a hook has to be.
   */
  useBarTitle(round ? GAMES[round.key].title : null)

  if (!room) {
    // The error has to be reachable from here too. Without it a refused
    // room:create leaves the television on "opening a game" forever, with the
    // explanation computed and never shown — indistinguishable from a hang.
    return (
      /*
       * These centre themselves, since the frame no longer centres anything.
       *
       * Dropping the frame's centring is what stopped a tall board being
       * pushed off the top (DOL-100), and the cost is that short content now
       * sits against the bar with a screen of nothing under it — which is what
       * a page that failed to load looks like. Every other state on this
       * screen has enough in it not to care; these two are a single line, and
       * they are the two somebody stares at while wondering whether it is
       * broken. `my-auto` is safe here precisely because they are short: it
       * collapses to nothing rather than clipping if they ever are not.
       */
      <div className="my-auto flex flex-col items-center gap-4">
        <p role="status" className="text-(length:--text-title) font-display">
          {status === 'connected' ? 'Opening a game…' : 'Getting things ready…'}
        </p>
        {error ? (
          <p role="alert" className="text-coral text-(length:--text-heading)">
            {friendlyRoomError(error)}
          </p>
        ) : null}
      </div>
    )
  }

  /**
   * Ends the room and goes back to the solo games.
   *
   * A declaration rather than a const so the key handler above can call it:
   * it only ever runs on a keypress, long after this render has finished.
   *
   * The order matters. Telling the server first means the phones are told
   * while there is still a room to tell them about; forgetting it before
   * leaving means the television cannot come back and try to resume a code it
   * has just killed — which would be refused and open a new room anyway, but
   * only after showing a dead one for a moment.
   */
  /** Starts a round, and remembers what it was. */
  function startRound(options: RoundOptions): void {
    if (!room) return

    lastDealt.current = options
    emit('room:start', { code: room.code, options })
  }

  /**
   * Another go at the same game, which is overwhelmingly what a room wants.
   *
   * With nothing remembered — a television that has just resumed a room it did
   * not start — the round is cleared instead, which lands on the menu with the
   * game a press away. Guessing a race's difficulty for three children would
   * be worse than asking.
   */
  function playAgain(): void {
    if (!room) return

    if (lastDealt.current === null) return backToTheMenu()

    startRound(lastDealt.current)
  }

  /** Done with this game: clear the round and land on the room's own menu. */
  function backToTheMenu(): void {
    if (!room) return

    emit('room:endRound', { code: room.code })
  }

  function closeTheRoom(): void {
    if (!room) return

    /*
     * Ask, and leave when the server says it is done — see `onClosed`.
     *
     * The obvious order, emit then leave, is a race this loses on a real
     * network: leaving disconnects the socket, and the message never gets out.
     * Waiting for the answer also means the phones are told before the
     * television goes, which is the whole point of closing rather than just
     * walking away from it.
     */
    closingOnPurpose.current = true
    emit('room:close', { code: room.code })
  }

  /*
   * The asking takes the whole screen.
   *
   * Not a panel over the board: this is the one irreversible thing the room
   * can do, and the board underneath is what somebody would be reading instead
   * of the question. Everything else on this screen is a round that can be
   * played again.
   */
  if (confirmingClose) {
    return (
      <ConfirmClose
        seated={room.players.length}
        onConfirm={closeTheRoom}
        onBack={() => setConfirmingClose(false)}
      />
    )
  }

  return (
    /*
     * The screen fills the frame at television scale, so the board inside it
     * has a height to divide. Without this the board sized itself to its own
     * contents and the frame carried the difference — which with three people
     * seated and twenty cards is most of a screen.
     */
    <div className="flex w-full flex-col items-center gap-10 [@media(min-height:900px)]:h-full [@media(min-height:900px)]:min-h-0 [[data-scale=tv]_&]:gap-4">
      {status === 'reconnecting' ? (
        // Silence here looks exactly like the game having crashed.
        <p role="alert" className="text-amber text-(length:--text-heading)">
          Reconnecting…
        </p>
      ) : null}

      {round === null ? (
        <Lobby room={room} />
      ) : round.key === 'hangman' ? (
        <Board game={round.view} players={room.players} turnEndsAt={room.turnEndsAt} />
      ) : round.key === 'memory' ? (
        <MemoryBoard game={round.view} players={room.players} turnEndsAt={room.turnEndsAt} />
      ) : (
        <RaceBoard game={round.view} players={room.players} />
      )}

      {choosing ? <GameChooser onStart={startRound} onLeave={() => leaveOnEscape(false)} /> : null}

      {finished ? <RoundOver onAgain={playAgain} onMenu={backToTheMenu} /> : null}

      {round !== null ? (
        <p className="text-ink-muted text-(length:--text-small)">
          Room {room.code} · {room.players.length} playing
        </p>
      ) : null}

      {/*
        The keys that are actually live, which differ by what is on screen.
        The chooser draws its own; this covers the other two states.

        Escape reads differently in each, and says so. Mid-round it *ends* the
        round to get back to the menu; at a result it just goes; with no round
        at all — a lobby nobody has joined yet — it leaves the room entirely.

        Three states and three sentences, because a key named without saying
        what it costs is how somebody loses a round they were in the middle of
        — and because an empty lobby offering to "end the round" is offering
        something that does not exist. Caught by looking at the screen rather
        than at the number beside it.
      */}
      {choosing ? null : (
        <KeyLegend
          shortcuts={
            finished
              ? [
                  { keys: ['Enter'], does: 'Another go' },
                  { keys: ['Esc'], does: 'Back to the games' },
                  CLOSE_SHORTCUT,
                ]
              : round !== null
                ? [{ keys: ['Esc'], does: 'End the round' }, CLOSE_SHORTCUT]
                : [LEAVE_SHORTCUT, CLOSE_SHORTCUT]
          }
        />
      )}

      {/*
        And a link as well, for the host that is not the television.

        The legend hides itself wherever the pointer is coarse, and Escape
        needs a keyboard — so a tablet propped up as the host had a way in and
        no way out at all. `LeaveGame` is the mirror image: hidden at TV scale,
        where the legend has it covered. Between them every host has one.
      */}
      <LeaveGame />

      {error ? (
        <p role="alert" className="text-coral text-(length:--text-heading)">
          {friendlyRoomError(error)}
        </p>
      ) : null}
    </div>
  )
}
